import { useQuery } from "@tanstack/react-query";
import { supabase } from '@/lib/supabase/client';
import { Mode } from "@/hooks/useMode";

import { useActiveProject } from "@/hooks/useActiveProject";
// Le module 'useActiveProject' ne peut pas être localisé, donc il est supprimé.

export interface AgentPerformanceData {
  agent_id: number;
  agent_name: string;
  team: string;
  total_calls: number;
  conversion_rate: number;
  satisfaction_rate: number;
  resolution_rate: number;
  performance_rate: number;
  average_duration: string;
  unusable_calls_rate: number;
  previous_conversion_rate: number;
  previous_satisfaction_rate: number;
  previous_resolution_rate: number;
  previous_performance_rate: number;
  previous_average_duration: string;
  previous_unusable_calls_rate: number;
  previous_total_calls: number;
  reason_performance: {
    stage: string;
    score: number;
    total_calls: number;
  }[];
}

interface UsePerformanceDataProps {
  mode?: Mode;
  projectId?: number | null;
  startDate?: Date | null;
  endDate?: Date | null;
  includeAgentMetrics?: boolean;
  includeStagePerformance?: boolean;
}

type AgentMetricsResponse = {
  agent_id: number;
  agent_name: string;
  team: string;
  total_calls: number;
  conversion_rate: number;
  satisfaction_rate: number;
  resolution_rate: number;
  performance_rate: number;
  unusable_calls_rate: number;
};

type StageMetricsResponse = {
  stage: string;
  total_calls: number;
  conversion_rate: number;
};

type GlobalMetricsResponse = {
  total_calls: number;
  conversion_rate: number;
  satisfaction_rate: number;
  resolution_rate: number;
  performance_rate: number;
  average_duration: string;
  unusable_calls_rate: number;
  reiteration_rate: number;
};

interface PerformanceDataResponse {
  globalMetrics: GlobalMetricsResponse;
  agents: AgentPerformanceData[];
  stagePerformance: {
    stage: string;
    score: number;
    total_calls: number;
  }[];
}

export const usePerformanceData = ({
  mode = 'experience',
  projectId = null,
  startDate = null,
  endDate = null,
  includeAgentMetrics = true,
  includeStagePerformance = true,
}: UsePerformanceDataProps = {}) => {
  const { activeProjectId } = useActiveProject();
  const finalProjectId = projectId ?? (activeProjectId ? Number(activeProjectId) : null);
  const startDateIso = startDate?.toISOString() ?? null;
  const endDateIso = endDate?.toISOString() ?? null;

  return useQuery({
    queryKey: [
      "performance-data",
      mode,
      finalProjectId,
      startDateIso,
      endDateIso,
      includeAgentMetrics,
      includeStagePerformance,
    ],
    queryFn: async (): Promise<PerformanceDataResponse> => {
      const [
        { data: globalMetricsData, error: globalError },
        { data: agentMetricsData, error: agentError },
        { data: stageData, error: stageError },
      ] = await Promise.all([
        supabase.rpc('calculate_global_metrics', {
          p_project_id: finalProjectId,
          p_start_date: startDateIso,
          p_end_date: endDateIso
        }),
        includeAgentMetrics
          ? supabase.rpc('calculate_agent_metrics', {
              p_project_id: finalProjectId,
              p_start_date: startDateIso,
              p_end_date: endDateIso
            })
          : Promise.resolve({ data: [], error: null }),
        includeStagePerformance
          ? supabase.rpc('calculate_complete_stage_performance', {
              p_mode: mode === 'sales' ? 'ameliorer_ventes' : 'optimiser_experience',
              p_project_id: finalProjectId,
              p_start_date: startDateIso,
              p_end_date: endDateIso
            })
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (globalError) {
        console.error('❌ Error fetching global metrics:', globalError);
        throw globalError;
      }
      if (agentError) throw agentError;
      if (stageError) throw stageError;

      // Structure pour les métriques globales
      // La fonction RPC peut retourner un tableau ou un objet, potentiellement imbriqué
      let metricsData: any = {};

      if (Array.isArray(globalMetricsData) && globalMetricsData.length > 0) {
        metricsData = globalMetricsData[0];
      } else if (globalMetricsData && typeof globalMetricsData === 'object') {
        metricsData = globalMetricsData;
      }

      // Gérer le cas où la réponse est encapsulée dans le nom de la fonction
      if (metricsData.calculate_global_metrics) {
        metricsData = metricsData.calculate_global_metrics;
      }
      const globalMetrics: GlobalMetricsResponse = {
        total_calls: metricsData.total_calls || 0,
        conversion_rate: metricsData.conversion_rate || 0,
        satisfaction_rate: metricsData.satisfaction_rate || 0,
        resolution_rate: metricsData.resolution_rate || 0,
        performance_rate: metricsData.performance_rate || 0,
        average_duration: metricsData.average_duration || "0m 0s",
        unusable_calls_rate: metricsData.unusable_calls_rate || 0,
        reiteration_rate: metricsData.reiteration_rate || 0
      };

      // Formater les données par étape
      const stagePerformance = ((stageData || []) as StageMetricsResponse[]).map((stage) => ({
        stage: stage.stage,
        score: stage.conversion_rate,
        total_calls: stage.total_calls
      }));

      // Formater les données par agent
      const agents: AgentPerformanceData[] = ((agentMetricsData || []) as AgentMetricsResponse[]).map((agent) => ({
        ...agent,
        average_duration: "0m 0s", // À définir si nécessaire par agent
        previous_average_duration: "0m 0s",
        previous_conversion_rate: 0,
        previous_satisfaction_rate: 0,
        previous_resolution_rate: 0,
        previous_performance_rate: 0,
        previous_total_calls: 0,
        previous_unusable_calls_rate: 0,
        reason_performance: stagePerformance,
      }));

      // Retourner les trois types de données organisées
      return {
        globalMetrics,
        agents,
        stagePerformance
      };
    },
    enabled: finalProjectId !== null,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
